// pages/vpc-project.js
import React, { useRef } from 'react';

const screenshots = [
  'Screenshot_2025-06-24_180522.png',
  'Screenshot_2025-06-24_180725.png',
  'Screenshot_2025-06-24_180733.png',
  'Screenshot_2025-06-24_180743.png',
  'Screenshot_2025-06-24_180830.png',
  'Screenshot_2025-06-24_180912.png',
  'Screenshot_2025-06-24_180925.png',
  'Screenshot_2025-06-24_180942.png',
  'Screenshot_2025-06-24_181030.png',
  'Screenshot_2025-06-24_181039.png',
  'Screenshot_2025-06-24_181059.png',
  'Screenshot_2025-06-24_181119.png',
  'Screenshot_2025-06-24_181137.png',
  'Screenshot_2025-06-24_181246.png',
  'Screenshot_2025-06-24_181256.png',
  'Screenshot_2025-06-24_181446.png',
  'Screenshot_2025-06-24_181455.png',
  'Screenshot_2025-06-24_181726.png',
  'Screenshot_2025-06-24_181736.png',
  'Screenshot_2025-06-24_181917.png',
  'Screenshot_2025-06-24_181926.png',
  'Screenshot_2025-06-24_181959.png',
  'Screenshot_2025-06-24_182007.png',
  'Screenshot_2025-06-24_182057.png',
  'Screenshot_2025-06-24_182758.png',
  'Screenshot_2025-06-24_182812.png',
  'Screenshot_2025-06-24_182819.png',
  'Screenshot_2025-06-24_182830.png'
];

const VpcProjectPage = () => {
  const scrollRef = useRef(null);

  const scrollLeft = () => {
    scrollRef.current.scrollBy({ left: -window.innerWidth, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollRef.current.scrollBy({ left: window.innerWidth, behavior: 'smooth' });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto text-white space-y-12">
      <h1 className="text-4xl font-bold mb-6">ETS Tracking System</h1>
      <p className="text-lg text-gray-300">
        This project demonstrates the end-to-end provisioning of AWS infrastructure for hosting a Java-React based ETS (Employee Tracking System) application using Terraform and GitLab CI/CD. The entire infrastructure—including VPCs, subnets, EC2s, ALBs, and RDS—is provisioned using Terraform. The Terraform files are automatically applied by a Jenkins pipeline as part of the CI/CD process.
      </p>

      <h2 className="text-2xl font-semibold mt-6">🔗 GitLab Repository</h2>
      <p className="text-gray-300">
        You can find the complete Terraform code for this infrastructure setup in this GitLab repository: <a href="https://gitlab.com/jenkins1315417/ets-terraform.git" className="text-blue-400 underline" target="_blank">ets-terraform</a>
      </p>

      <h2 className="text-2xl font-semibold mt-6">📌 Jenkins & SonarQube Architecture</h2>
      <p className="text-gray-300">
        Jenkins was deployed in the <strong>default VPC</strong> for ease of access and initial configuration. SonarQube, however, was securely hosted in a <strong>different custom VPC</strong> to isolate scanning operations. A route was configured between these VPCs using VPC peering to allow Jenkins to communicate with SonarQube over private IPs.
      </p>
      <p className="text-gray-300">
        This design ensures vulnerability scans from Jenkins (in the default VPC) can reach SonarQube (in the isolated VPC), allowing SonarQube to analyze and match known vulnerabilities efficiently. This routing architecture ensures security while enabling full CI/CD and DevSecOps capabilities.
      </p>

      <h2 className="text-2xl font-semibold mt-6">🐳 Jenkins Docker Image Build & Deployment</h2>
      <p className="text-gray-300">
        In this setup, Jenkins (in the default VPC) builds a Docker image of the backend Java application and pushes it to Docker Hub. The actual EC2 instance hosting the container runs in a <strong>private subnet</strong> of a <strong>custom VPC</strong>. In order for Jenkins to securely deploy the application to this EC2 instance, we established <strong>VPC Peering</strong> between the default and custom VPCs. Route tables were updated to allow Jenkins to reach the private EC2 using its private IP address.
      </p>
      <p className="text-gray-300">
        After the image is pushed to Docker Hub, the EC2 instance in the private subnet pulls the image and runs the containerized Java application. This ensures full automation from code to deployment, maintaining network isolation and security boundaries.
      </p>

      <h2 className="text-2xl font-semibold mt-6">🌐 Infrastructure Design</h2>
      <ul className="list-disc list-inside text-gray-300">
        <li>Custom VPC with CIDR <code>10.0.0.0/16</code></li>
        <li>2 Public Subnets: For NGINX and React frontend servers</li>
        <li>2 Private Subnets: For Spring Boot backend and RDS MySQL database</li>
        <li>Internet Gateway & NAT Gateway with proper route tables</li>
        <li>Security groups for ALB, EC2 frontend, and backend</li>
        <li>VPC Peering with appropriate route table entries</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-6">🖥 EC2 & Application Deployment</h2>
      <ul className="list-disc list-inside text-gray-300">
        <li>2 EC2s in public subnet running frontend React app with NGINX</li>
        <li>2 EC2s in private subnet pulling and running Spring Boot Docker containers</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-6">🔁 Load Balancer & Target Group</h2>
      <p className="text-gray-300">
        ALB listens on port 80 and forwards traffic to backend instances via target groups with HTTP health checks.
      </p>

      <h2 className="text-2xl font-semibold mt-6">🔐 Security Groups</h2>
      <ul className="list-disc list-inside text-gray-300">
        <li>ALB SG: Allows HTTP/HTTPS from 0.0.0.0/0</li>
        <li>Frontend SG: Allows SSH, HTTP, HTTPS</li>
        <li>Backend SG: Allows HTTP, SSH, and Docker Hub outbound</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-6">🧪 CI/CD Automation</h2>
      <ul className="list-disc list-inside text-gray-300">
        <li>Terraform code executed by Jenkins pipeline to provision infra</li>
        <li>Frontend React code to NGINX in public EC2</li>
        <li>Backend Java app built into Docker image by Jenkins</li>
        <li>Docker image pushed to Docker Hub</li>
        <li>Private EC2 pulls image and runs container</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-6">🛢 MySQL on RDS</h2>
      <p className="text-gray-300">
        RDS MySQL runs in a private subnet with access restricted to backend EC2 only.
      </p>

      <h2 className="text-2xl font-semibold mt-6">🛠 Tech Stack</h2>
      <ul className="list-disc list-inside text-gray-300">
        <li>AWS: VPC, EC2, ALB, RDS, EIP, NAT, IGW</li>
        <li>Terraform for IaC</li>
        <li>React + NGINX for frontend</li>
        <li>Java Spring Boot for backend</li>
        <li>MySQL (RDS)</li>
        <li>GitLab CI/CD</li>
        <li>Docker + Docker Hub</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-6">🚀 Conclusion</h2>
      <p className="text-gray-300">
        This project showcases the deployment of a secure, scalable infrastructure using Terraform, including a complete CI/CD pipeline with Docker-based deployments and cloud-native best practices.
      </p>

      <h2 className="text-3xl font-bold mb-4">📂 All Screenshots</h2>
      <div className="relative w-full overflow-hidden">
        <button onClick={scrollLeft} className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-slate-700 px-3 py-2 rounded-full shadow-lg text-white">◀</button>
        <div
          ref={scrollRef}
          className="overflow-x-scroll flex snap-x snap-mandatory scroll-smooth w-full"
          style={{ scrollSnapType: 'x mandatory', overflowY: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {screenshots.map((src, idx) => (
            <div
              key={idx}
              className="snap-center flex-shrink-0 w-full flex flex-col items-center px-2"
            >
              <img
                src={`/ETS/${src}`}
                alt={`Screenshot ${idx + 1}`}
                className="object-contain w-full h-auto max-h-[600px] border border-gray-700 rounded-md shadow-md"
              />
            </div>
          ))}
        </div>
        <button onClick={scrollRight} className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-slate-700 px-3 py-2 rounded-full shadow-lg text-white">▶</button>
      </div>

      <style>
        {`
          ::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
    </div>
  );
};

export default VpcProjectPage;
